import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { AuthLayout } from "@/features/auth/components/AuthLayout";
import { useAppForm } from "@/lib/form/form-hook";
import { createForgotPasswordSchema } from "@/lib/form/schemas/auth";
import { useForgotPassword } from "@/features/auth/hooks/useForgotPassword";
import { Mail } from "lucide-react";

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const { t } = useTranslation();
  const mutation = useForgotPassword();
  const schema = createForgotPasswordSchema(t);
  const [submitted, setSubmitted] = useState(false);

  const form = useAppForm({
    defaultValues: {
      email: "",
    },
    validators: {
      onBlur: schema,
    },
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value }) => {
      try {
        await mutation.mutateAsync(value);
      } catch {
        // Intentionally show success regardless (prevent email enumeration)
      }
      setSubmitted(true);
    },
  });

  // Success state — always shown to prevent email enumeration
  if (submitted) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-info/10">
            <Mail size={32} className="text-info" />
          </div>
          <p className="text-sm text-muted">{t("auth.forgotPassword.success")}</p>
          <Link
            to="/login"
            className="mt-2 text-sm font-medium text-indigo hover:text-indigo-bright transition-colors"
          >
            {t("auth.forgotPassword.backToLogin")}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {/* Header */}
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-bold text-heading">{t("auth.forgotPassword.title")}</h1>
        <p className="mt-1 text-sm text-muted">{t("auth.forgotPassword.subtitle")}</p>
      </div>

      {/* Form */}
      <form.AppForm>
        <form.Form className="flex flex-col gap-4" noValidate>
          <form.AppField
            name="email"
            children={(field) => (
              <field.TextField
                label={t("auth.forgotPassword.email")}
                type="email"
                placeholder={t("auth.forgotPassword.emailPlaceholder")}
                autoComplete="email"
              />
            )}
          />

          <form.SubmitButton
            label={t("auth.forgotPassword.submit")}
            loadingLabel={t("auth.forgotPassword.submitting")}
          />
        </form.Form>
      </form.AppForm>

      {/* Back to login */}
      <p className="mt-6 text-center text-sm text-muted">
        <Link
          to="/login"
          className="font-medium text-indigo hover:text-indigo-bright transition-colors"
        >
          {t("auth.forgotPassword.backToLogin")}
        </Link>
      </p>
    </AuthLayout>
  );
}
